import * as React from "react"
import { CartItem } from "types/cart"
import BroadcastChannel from "broadcast-channel"
import { useSyncedReducer } from "hooks"

const channel = new BroadcastChannel<Action>("cart")

export const CartContext = React.createContext<CartItem[]>([])
export const CartContextSetState = React.createContext<React.Dispatch<Action>>(
	() => {}
)

function deleteItem(state: CartItem[], id: number) {
	return state.filter((item) => item.id !== id)
}

function addQty(state: CartItem[], id: number) {
	return state.map((item) => {
		if (item.id === id) {
			return { ...item, quantity: item.quantity + 1 }
		}
		return item
	})
}

function subQty(state: CartItem[], id: number) {
	const index = state.findIndex(
		(item) => item.id === id && item.quantity === 1
	)
	if (index !== -1) {
		return deleteItem(state, id)
	}
	return state.map((item) => {
		if (item.id === id) {
			return { ...item, quantity: item.quantity - 1 }
		}
		return item
	})
}

function addItem(state: CartItem[], { id, quantity }: CartItem) {
	if (state.filter((item) => item.id == id).length === 0) {
		return [...state, { id, quantity }]
	}
	return addQty(state, id)
}

type Action =
	| { type: "init"; payload: CartItem[] }
	| { type: "new"; payload: CartItem }
	| { type: "delete"; id: number }
	| { type: "add"; id: number }
	| { type: "sub"; id: number }
	| { type: "clean" }

const reducer = (state: CartItem[], action: Action) => {
	switch (action.type) {
		case "init":
			return [...action.payload]
		case "new":
			return addItem(state, action.payload)
		case "delete":
			return deleteItem(state, action.id)
		case "add":
			return addQty(state, action.id)
		case "sub":
			return subQty(state, action.id)
		case "clean":
			return []
		default:
			throw new Error("Unexpected action")
	}
}

const localStorageKey = "cartItems"

const CartProvider: React.FC = ({ children }) => {
	const [state, dispatch] = useSyncedReducer(channel, reducer, [])

	// initlize reducer after rehydration
	React.useEffect(() => {
		const localStorageCartItems = localStorage.getItem(localStorageKey)
		if (localStorageCartItems !== null) {
			dispatch({
				type: "init",
				payload: JSON.parse(localStorageCartItems) as CartItem[],
			})
		}
	}, [])

	React.useEffect(() => {
		localStorage.setItem(localStorageKey, JSON.stringify(state))
	}, [state])

	return (
		<CartContextSetState.Provider value={dispatch}>
			<CartContext.Provider value={state}>
				{children}
			</CartContext.Provider>
		</CartContextSetState.Provider>
	)
}

export default CartProvider
